import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay, finalize } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../projects/my-app/src/environments/environment';

const QTR_PAYLOAD_TRACE_KEY = 'QTR_PAYLOAD_TRACE_V1';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  protected router = inject(Router);
  // simple in-memory map to deduplicate identical concurrent requests
  protected inProgressRequests: Map<string, Observable<any>> = new Map();


  /**
   * In-flight request cache.
   * Key = method + URL + payload string + query params.
   */
  private inFlightRequests = new Map<string, Observable<any>>();

  constructor(protected http: HttpClient) { }

  protected resolveRequestUrl(url: string): string {
    const isLocalAssets = url.startsWith('assets') || url.startsWith('/assets');
    const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);

    if (isLocalAssets || isAbsoluteUrl) {
      return url;
    }

    const baseUrl = environment.URL.endsWith('/')
      ? environment.URL.slice(0, -1)
      : environment.URL;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  }

  protected snapshot<T>(value: T): T {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }

  /** Exclude internal keys (UI state, widget records) from API request params */
  private static readonly INTERNAL_KEYS = new Set(['loading']);
  protected cleanRequestParams(params: any): any {
    if (!params) return {};
    const cleaned: any = {};
    for (const key of Object.keys(params)) {
      if (ConfigService.INTERNAL_KEYS.has(key)) continue;
      if (key.startsWith('widget-')) continue;
      cleaned[key] = params[key];
    }
    return cleaned;
  }

  protected getChangedFilterKeys(changedFilterKey: any): string[] {
    if (Array.isArray(changedFilterKey)) {
      return changedFilterKey
        .map((key) => String(key || '').trim().toLowerCase())
        .filter(Boolean);
    }

    return String(changedFilterKey || '')
      .split(',')
      .map((key) => key.trim().toLowerCase())
      .filter(Boolean);
  }

  protected pruneSiblingTimeFilters(
    params: Record<string, any> | undefined,
    changedFilterKey: any,
  ): Record<string, any> {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
      return {};
    }

    return { ...params };
  }

  protected getPayloadFilterOperator(
    attributeName: string,
    values: any[],
    currentOperator?: string,
  ): string {
    const normalizedKey = String(attributeName || '').trim().toLowerCase();
    if (['year', 'qtr', 'quarter'].includes(normalizedKey)) {
      return 'IN';
    }

    if (currentOperator && !['=', 'IN'].includes(currentOperator)) {
      return currentOperator;
    }

    return values.length > 1 ? 'IN' : '=';
  }

  protected normalizeTimeFilterWhere(whereConditions: any[]): any[] {
    if (!Array.isArray(whereConditions) || whereConditions.length === 0) {
      return [];
    }

    const hasMonth = whereConditions.some(
      (item: any) => String(item?.attributeName || '').trim().toLowerCase() === 'month',
    );
    const timeFilterOrder = new Map([
      ['month', 0],
      ['year', 1],
      ['qtr', 2],
      ['quarter', 2],
    ]);

    const hasExplicitOr = whereConditions.some(
      (item: any) => String(item?.logicalOperator || '').trim().toUpperCase() === 'OR',
    );
    const normalized = whereConditions
      .map((item: any) => {
        const attributeValues = Array.isArray(item?.attributeValues)
          ? item.attributeValues
          : [item?.attributeValues].filter(
              (value) => value !== undefined && value !== null && value !== '',
            );

        return {
          ...item,
          attributeValues,
          operator: this.getPayloadFilterOperator(
            item?.attributeName,
            attributeValues,
            item?.operator,
          ),
        };
      })
      .sort((a: any, b: any) => {
        if (!hasMonth || hasExplicitOr) {
          return 0;
        }

        const keyA = String(a?.attributeName || '').trim().toLowerCase();
        const keyB = String(b?.attributeName || '').trim().toLowerCase();
        return (timeFilterOrder.get(keyA) ?? 99) - (timeFilterOrder.get(keyB) ?? 99);
      });

    if (!hasExplicitOr) {
      normalized.forEach((item: any, index: number) => {
        item.logicalOperator = index < normalized.length - 1 ? 'AND' : '';
      });
    }

    return normalized;
  }

  protected getCustomPayloadBase(customPayload: any): any {
    if (customPayload === true || customPayload === 'true') {
      return {
        filter: {
          where: [],
          join: {},
          orderBy: {},
        },
      };
    }

    return JSON.parse(JSON.stringify(customPayload || {}));
  }

  protected shouldTraceQuarterFlow(url?: string, data?: any, filters?: any): boolean {
    const normalizedUrl = String(url || '');
    const changedKeys = this.getChangedFilterKeys(filters?.changedFilterKey);
    const requestKeys = Object.keys(filters?.requestParams || {}).map((key) => key.toLowerCase());
    const queryKeys = Object.keys(filters?.queryParams || {}).map((key) => key.toLowerCase());
    const dataWidgetId = String(data?.widgetId || '').toLowerCase();
    const contentKeys = new Set(
      (data?.content || [])
        .map((item: any) => String(item?.key || '').trim().toLowerCase())
        .filter(Boolean),
    );

    return (
      normalizedUrl.includes('/qtr-year/drop-down') ||
      normalizedUrl.includes('/customer-drop-down') ||
      changedKeys.some((key) => ['year', 'qtr', 'quarter', 'month'].includes(key)) ||
      requestKeys.some((key) => ['year', 'qtr', 'quarter', 'month'].includes(key)) ||
      queryKeys.some((key) => ['year', 'qtr', 'quarter', 'month'].includes(key)) ||
      dataWidgetId.includes('quarterly') ||
      (contentKeys.has('year') && contentKeys.has('qtr') && contentKeys.has('month'))
    );
  }

  protected traceQuarterFlow(stage: string, details: any): void {
    console.log(`[${QTR_PAYLOAD_TRACE_KEY}] ${stage}`, this.snapshot(details));
  }

  getDashboardConfig(configFileName: string): Observable<any> {
    return this.http.get(`/assets/dashboard-config/${configFileName}`);
  }

  protected buildPayload(requestParamFilters: any): any {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/') ? currentPath.substring(1) : currentPath;
    if (
      dashboardName === 'europe-impact-report' &&
      (!requestParamFilters || Object.keys(requestParamFilters).length === 0)
    ) {
      return {
        filter: {
          where: [],
          join: {},
          orderBy: {},
        },
      };
    }
    let staticFilters: any[] = [];
    const customerNameValue = requestParamFilters?.['customer_name'];

    if (dashboardName === 'customer-overview') {
      // added condition to filter out default customername in payload if customer name is selected
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (dashboardName === 'cspec-bridge') {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    } else if (
      dashboardName !== 'opportunity-overview' &&
      dashboardName !== 'europe-impact-report'
    ) {
      if (
        !customerNameValue &&
        (!Array.isArray(customerNameValue) || customerNameValue.length > 0)
      ) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    }

    const dynamicFilters = Object.entries(requestParamFilters)
      .map(([key, value]) => {
        const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value]; // recursively flatten the array to any depth eg. [[[]]].
        let updatedValues = rawValues.filter(
          (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
        );

        if (updatedValues?.length === 0) return null;

        return {
          attributeName: key,
          operator: this.getPayloadFilterOperator(key, updatedValues),
          attributeValues: updatedValues,
          logicalOperator: 'AND',
        };
      })
      .filter((filter): filter is NonNullable<typeof filter> => filter !== null); //emoves null entries from the array.

    // Adjust logicalOperator handling
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else {
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = '';
      }
    }

    return {
      filter: {
        where: this.normalizeTimeFilterWhere([...staticFilters, ...dynamicFilters]),
        join: {},
        orderBy: {},
      },
    };
  }

  protected buildQueryParams(queryParamFilters: any): HttpParams {
    let params = new HttpParams();
    for (const key in queryParamFilters) {
      const value = queryParamFilters[key];

      if (key === 'slider_range' && Array.isArray(value) && value.length === 2) {
        const start = parseInt(value[0], 10);
        const end = parseInt(value[1], 10);

        params = params.set('startRange', start.toString());
        console.log('Start Range:', start);
        params = params.set('endRange', end.toString());
      } else if (key === 'button') {
        params = params.set(value[0], true);
      } else {
        params = params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    }

    return params;
  }

  getConfig(
    url: string,
    data?: any,
    filters?: any,
    method?: string,
    isTable = false,
  ): Observable<any> {
    const shouldTraceQuarterFlow = this.shouldTraceQuarterFlow(url, data, filters);
    console.log('[ConfigService.getConfig] Called with:', { url, dataWidgetId: data?.widgetId, filters, method });

    const queryParamsForHttp = this.pruneSiblingTimeFilters(
      { ...(filters?.queryParams || {}) },
      filters?.changedFilterKey,
    );
    const rawQueryString = filters?.rawQueryString;

    console.log('lclick 39 inside getConfig config service', url, data, filters, method, isTable);

    // Embed Query Params into URL
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];
      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }
      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    // Payload Logic
    let payload: any = null;
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    const filteredRequestParams = this.pruneSiblingTimeFilters(
      this.cleanRequestParams(filters?.requestParams),
      filters?.changedFilterKey,
    );
    const hasFilteredRequestParams = Object.keys(filteredRequestParams).length > 0;

    if (shouldTraceQuarterFlow) {
      this.traceQuarterFlow('configService:getConfig:input', {
        url,
        widgetId: data?.widgetId,
        changedFilterKey: filters?.changedFilterKey,
        queryParamsForHttp,
        filteredRequestParams,
        rawFilters: filters,
      });
    }

    if (data?.skipDefaultPayload) {
      // When skipDefaultPayload is true, still merge filters into customPayload
      payload = this.getCustomPayloadBase(data?.customPayload);

      // Merge request params into the payload's requestData or filter.where
      if (hasFilteredRequestParams) {
        if (payload.requestData) {
          payload.requestData = {
            ...payload.requestData,
            ...filteredRequestParams,
          };
        } else if (payload.filter && Array.isArray(payload.filter.where)) {
          Object.keys(filteredRequestParams).forEach((key) => {
            const rawValues = Array.isArray(filteredRequestParams[key])
              ? filteredRequestParams[key].flat(Infinity)
              : [filteredRequestParams[key]];
            const values = rawValues.filter(
              (v: any) =>
                v !== null &&
                v !== undefined &&
                v !== '' &&
                !(Array.isArray(v) && v.length === 0),
            );

            // If the values are empty, we should remove the existing filter if it exists
            const existingIndex = payload.filter.where.findIndex(
              (w: any) => w.attributeName === key,
            );

            if (values.length === 0) {
              if (existingIndex !== -1) {
                payload.filter.where.splice(existingIndex, 1);
              }
              return;
            }

            if (existingIndex !== -1) {
              payload.filter.where[existingIndex].attributeValues = values;
              payload.filter.where[existingIndex].operator =
                this.getPayloadFilterOperator(key, values);
            } else {
              payload.filter.where.push({
                attributeName: key,
                operator: this.getPayloadFilterOperator(key, values),
                attributeValues: values,
              });
            }
          });
          payload.filter.where = this.normalizeTimeFilterWhere(payload.filter.where);
        }
      }
    } else {
      payload = {
        requestData: {
          ...(!hasFilteredRequestParams ? defaultRequestData : {}),
          ...filteredRequestParams,
          ...(data?.customPayload || {}),
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: data?.version || '1.0',
      };
    }

    console.log('[ConfigService.getConfig] Built payload:', payload);

    if (shouldTraceQuarterFlow) {
      this.traceQuarterFlow('configService:getConfig:payloadBuilt', {
        url,
        widgetId: data?.widgetId,
        payload,
      });
    }

    // Query Params Logic
    const useQueryParams = filters?.useQueryParams === true || data?.useQueryParam === true;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    if (shouldTraceQuarterFlow) {
      this.traceQuarterFlow('configService:getConfig:requestReady', {
        url,
        widgetId: data?.widgetId,
        requestMethod: method || data?.method || 'post',
        queryParams: queryParams.toString(),
        payload,
      });
    }

    if (filters && 'useQueryParams' in filters) {
      delete filters.useQueryParams;
    }

    if (!!isTable) {
      // Table specific logic if needed in future
    }

    url = this.resolveRequestUrl(url);

    // Determine HTTP Method
    const requestMethod = method || data?.method || 'post';

    const dedupKey = `${requestMethod.toUpperCase()}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      requestMethod === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams })
        : this.http.get<any>(url, { params: queryParams })
    ).pipe(
      map((res) => {
        const unwrapped = this.unwrapResponse(res);
        if (shouldTraceQuarterFlow) {
          this.traceQuarterFlow('configService:getConfig:response', {
            url,
            widgetId: data?.widgetId,
            responseKeys: Object.keys(unwrapped || {}),
          });
        }
        return unwrapped;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  createPayload(
    filters: { [key: string]: any } = {},
    entity: string = '',
    filterName: string = '',
  ): any {
    const keys = Object.keys(filters);

    const where = keys.flatMap((key) => {
      const value = filters[key];

      // Check if the key is 'filter' and its value is an array of rule objects
      const isFilterRuleArray =
        key === 'filter' &&
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'attributeName' in value[0];

      if (isFilterRuleArray) {
        return value; // It's from the dialog, return the rules directly
      }

      // Process other simple filters (or a 'filter' that is not a rule array)
      const values = (Array.isArray(value) ? value : [value]).filter(
        (v) => v !== null && v !== undefined && v !== '' && v !== 'All',
      );

      if (values.length === 0) {
        return [];
      }

      return [
        {
          attributeName: key,
          operator: this.getPayloadFilterOperator(key, values),
          attributeValues: values,
          logicalOperator: 'AND', // Placeholder
        },
      ];
    });

    // Now, fix the logical operators for the entire 'where' clause
    if (where.length > 0) {
      for (let i = 0; i < where.length - 1; i++) {
        // If a rule from the dialog already has an operator (like OR), respect it.
        // Otherwise, set it to AND.
        if (!where[i].logicalOperator) {
          where[i].logicalOperator = 'AND';
        }
      }
      // The very last item in the whole clause has no logical operator.
      where[where.length - 1].logicalOperator = '';
    }

    return {
      filterName,
      entity,
    };
  }

  getApi(url: string, filters?: any, data?: any, method: 'post' | 'get' = 'post'): Observable<any> {
    const shouldTraceQuarterFlow = this.shouldTraceQuarterFlow(url, data, filters);
    const queryParamsForHttp = this.pruneSiblingTimeFilters(
      { ...(filters?.queryParams || {}) },
      filters?.changedFilterKey,
    );
    const rawQueryString = filters?.rawQueryString;

    //  Embed query params into URL path
    if (data?.embedQueryParams && Array.isArray(data.embeddedParams)) {
      const embeddedParts: string[] = [];

      for (const paramName of data.embeddedParams) {
        if (queryParamsForHttp[paramName]) {
          embeddedParts.push(queryParamsForHttp[paramName]);
          delete queryParamsForHttp[paramName];
        }
      }

      if (embeddedParts.length > 0) {
        const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        url = [baseUrl, ...embeddedParts].join('/');
      }
    }

    //  PAYLOAD LOGIC
    let payload: any = null;

    // Use defaultPayload only if no dynamic requestParams are provided
    const filteredReqParams = this.pruneSiblingTimeFilters(
      this.cleanRequestParams(filters?.requestParams),
      filters?.changedFilterKey,
    );
    const hasFilteredReqParams = Object.keys(filteredReqParams).length > 0;

    if (shouldTraceQuarterFlow) {
      this.traceQuarterFlow('configService:getApi:input', {
        url,
        widgetId: data?.widgetId,
        changedFilterKey: filters?.changedFilterKey,
        queryParamsForHttp,
        filteredReqParams,
      });
    }
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};

    if (data?.skipDefaultPayload) {
      //  Do NOT use base payload
      // Send only custom payload or empty object
      payload = this.getCustomPayloadBase(data?.customPayload);

      // Merge request params into the payload's requestData or filter.where
      if (hasFilteredReqParams) {
        if (payload.requestData) {
          payload.requestData = {
            ...payload.requestData,
            ...filteredReqParams,
          };
        } else if (payload.filter && Array.isArray(payload.filter.where)) {
          Object.keys(filteredReqParams).forEach((key) => {
            const values = Array.isArray(filteredReqParams[key])
              ? filteredReqParams[key]
              : [filteredReqParams[key]];

            const existing = payload.filter.where.find((w: any) => w.attributeName === key);
            if (existing) {
              existing.attributeValues = values;
              existing.operator = this.getPayloadFilterOperator(key, values);
            } else {
              payload.filter.where.push({
                attributeName: key,
                operator: this.getPayloadFilterOperator(key, values),
                attributeValues: values,
              });
            }
          });
          payload.filter.where = this.normalizeTimeFilterWhere(payload.filter.where);
        }
      }
    } else {
      //  USE BASE PAYLOAD
      payload = {
        requestData: {
          ...(!hasFilteredReqParams ? defaultRequestData : {}),
          ...filteredReqParams,
          ...(data?.customPayload || {}),
        },
        responseList: [],
        entity: data?.entity || 'default_entity',
        workflowId: data?.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: data?.functionName || 'DefaultService',
            version: data?.version || '1.0',
          },
        ],
        communicationType: 'rest',
        version: '1.0',
      };
    }

    // Query Params
    const useQueryParams = data?.useQueryParam !== false;
    let queryParams = new HttpParams();

    if (useQueryParams) {
      if (rawQueryString) {
        url = `${url}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    url = this.resolveRequestUrl(url);

    // API Call
    const cacheKey = `${method}|${url}|${JSON.stringify(payload)}|${queryParams.toString()}`;

    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    const request$ = (
      method === 'post'
        ? this.http.post<any>(url, payload, { params: queryParams })
        : this.http.get<any>(url, { params: queryParams })
    ).pipe(
      map((res) => {
        const unwrapped = this.unwrapResponse(res);
        if (shouldTraceQuarterFlow) {
          this.traceQuarterFlow('configService:getApi:response', {
            url,
            widgetId: data?.widgetId,
            responseKeys: Object.keys(unwrapped || {}),
          });
        }
        return unwrapped;
      }),
      shareReplay(1),
      finalize(() => this.inFlightRequests.delete(cacheKey))
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  getDropdownOptions(url: string, payload: any): Observable<any> {
    const where = payload?.filter?.where || [];
    const hasTimeFiltersInPayload = Array.isArray(where) &&
      where.some((item: any) =>
        ['year', 'qtr', 'quarter', 'month'].includes(
          String(item?.attributeName || '').trim().toLowerCase(),
        ),
      );
    const shouldTraceQuarterFlow =
      this.shouldTraceQuarterFlow(url, undefined, {
        requestParams: {},
        queryParams: {},
        changedFilterKey: undefined,
      }) || hasTimeFiltersInPayload;

    if (shouldTraceQuarterFlow) {
      this.traceQuarterFlow('configService:getDropdownOptions:request', {
        url,
        payload,
      });
    }

    url = this.resolveRequestUrl(url);
    const dedupKey = `DROPDOWN|${url}|${JSON.stringify(payload)}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = this.http.post<any>(url, payload).pipe(
      map((res) => {
        const unwrapped = this.unwrapResponse(res);
        if (shouldTraceQuarterFlow) {
          this.traceQuarterFlow('configService:getDropdownOptions:response', {
            url,
            responseKeys: Object.keys(unwrapped || {}),
            qtrOptions: unwrapped?.qtr,
            monthOptions: unwrapped?.month,
            yearOptions: unwrapped?.year,
          });
        }
        return unwrapped;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  protected unwrapResponse(response: any): any {
    if (response?.responseList?.length > 0) {
      const firstItem = response.responseList[0];
      if (firstItem?.data?.length > 0) {
        return firstItem.data[0];
      }
    }
    return response;
  }

  getData(url: string) {
    return this.http.get(url);
  }
}
